// ─── El panel de un clip de animación por código ─────────────────────────────
// Reemplaza al formulario de props de las plantillas cuando el clip trae
// `animationCodigo` (ADR-2026-09-24-animacion-por-codigo). No hay campos que
// editar a mano: la animación se pide y se cambia hablando con el agente. Lo que
// sí se toca acá es lo que también tienen las plantillas —tamaño, centrar— y el
// historial de versiones, para volver a una anterior con un toque.

import React from 'react';
import { Pencil, RotateCcw } from 'lucide-react';
import type { AnimacionPorCodigo } from './codigo/versiones';
import { CREDITOS_ANIMACION } from './codigo/reglas';

type Props = {
  nombre: string;
  animacion: AnimacionPorCodigo;
  /** Escala actual del clip (`animScale`), 0.05..1. */
  escala: number;
  onEscala: (escala: number) => void;
  onCentrar: () => void;
  onVolverAVersion: (indice: number) => void;
  /** Abre el chat con la animación ya nombrada en el input. */
  onEditar: () => void;
};

export const PanelAnimacionPorCodigo: React.FC<Props> = ({
  nombre,
  animacion,
  escala,
  onEscala,
  onCentrar,
  onVolverAVersion,
  onEditar,
}) => {
  const actual = animacion.versiones[animacion.actual];
  const pct = Math.round(escala * 100);
  return (
    <div className="space-y-4" data-clip-ui="true">
      <div className="px-1">
        <p className="text-[14px] font-medium text-brand-black/85">{nombre}</p>
        {actual?.resumen && (
          <p className="text-[12px] text-neutral-500 mt-0.5">{actual.resumen}</p>
        )}
        <p className="text-[11.5px] text-neutral-400 mt-1">
          {animacion.tipo === 'escena' ? 'Escena completa, con fondo propio.' : 'Superpuesta al video, con fondo transparente.'}
        </p>
      </div>

      {/* La animación se cambia hablando con el agente: el botón abre el chat con
          la animación ya nombrada y el usuario escribe qué quiere («más lento»,
          «con el logo», «en rojo»). */}
      <button
        type="button"
        onClick={onEditar}
        className="w-full py-2.5 px-5 inline-flex items-center justify-center gap-2 rounded-md text-[13px] font-medium bg-brand-primary text-white hover:bg-brand-primary/90 transition-colors"
        title="Pedirle un cambio al agente"
      >
        <Pencil className="w-4 h-4" /> Editar
        <span className="ml-1 text-[11px] font-semibold rounded-full bg-white/20 px-1.5 py-0.5">
          {CREDITOS_ANIMACION.modificar} créditos
        </span>
      </button>
      <p className="text-[11.5px] text-neutral-400 px-1 -mt-2">
        Cada cambio cuesta {CREDITOS_ANIMACION.modificar} créditos y queda como una versión.
        Volver a una versión anterior es gratis.
      </p>

      <div className="space-y-2 px-1">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-medium text-brand-black/70">Tamaño</span>
          <button
            type="button"
            onClick={onCentrar}
            className="text-[11.5px] text-brand-primary hover:underline"
            title="Volver al centro de la canvas"
          >
            Centrar
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={5}
            max={100}
            value={pct}
            onChange={(e) => onEscala(Number(e.target.value) / 100)}
            className="flex-1 accent-brand-primary"
            aria-label="Tamaño de la animación"
          />
          <span className="text-[12px] tabular-nums text-brand-black/70 w-10 text-right">{pct}%</span>
        </div>
      </div>

      {animacion.versiones.length > 1 && (
        <div className="space-y-1.5 px-1">
          <p className="text-[12px] font-medium text-brand-black/70">Versiones</p>
          <ul className="space-y-1">
            {animacion.versiones
              .map((v, i) => ({ v, i }))
              .reverse()
              .map(({ v, i }) => {
                const esActual = i === animacion.actual;
                return (
                  <li key={`${i}-${v.fecha}`}>
                    <button
                      type="button"
                      onClick={() => !esActual && onVolverAVersion(i)}
                      disabled={esActual}
                      className={`w-full text-left rounded-md px-2.5 py-2 text-[12px] transition-colors ${
                        esActual
                          ? 'bg-brand-primary/10 text-brand-black/85 cursor-default'
                          : 'hover:bg-neutral-100 text-brand-black/70'
                      }`}
                      title={esActual ? 'Es la versión que se ve' : 'Usar esta versión'}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-neutral-400 tabular-nums">v{i + 1}</span>
                        <span className="truncate flex-1">{v.resumen || 'Sin descripción'}</span>
                        {esActual ? (
                          <span className="text-[10.5px] uppercase tracking-wide text-brand-primary">actual</span>
                        ) : (
                          <RotateCcw className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
};
